// A care task's schedule (brief 1E-10; mockup v60 Care plan, Field app › Visit, Home Care ›
// Settings › Parts of the day; DECISIONS.md 10/26 "Care tasks: part of the day by default, a set
// time only when needed").
//
// - When in the day: any time during the visit, a part of the day (the company's: morning, midday,
//   afternoon, evening, night; night crosses midnight), or a set time with a window.
// - How often: every visit, on chosen days, a number of times a week on any day, once a week on a
//   day, or as needed.
// - Due this visit: every visit and any time is always due. A day rule is due only on its days (the
//   company's time zone). A part of the day (or a set time's window) is due on a visit that
//   overlaps it; when two or more of the patient's visits overlap the same one (24-hour care, split
//   shifts) it goes to the one overlapping it most, and a tie goes to the one covering its start,
//   so it shows on one visit only (Thomas, PR 40). Times a week stays due until the week's count is met. As needed is
//   never due.
// - Behind: a times-a-week task is behind as soon as the visits left in the company week can't
//   make up the count. A flag only; care is never held for it.
// - A visit reads the lines in effect for it: a changed line on a signed plan starts with the next
//   visit, and a visit already checked in keeps the line it had.
// Pure: no database, no clock (the moment and the company's zone are handed in).

import { addDays, dowOf, zonedDay, zonedInstant } from "../../../rules/week";

type Read<T> = { ok: true; value: T } | { ok: false; error: string };
const no = <T>(error: string): Read<T> => ({ ok: false, error });
const obj = (v: unknown): Record<string, unknown> | null =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
const MINUTE = 60_000;

export const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"] as const;

// ---- The parts of the day (a company setting) ------------------------------------------------------

export const PART_KEYS = ["morning", "midday", "afternoon", "evening", "night"] as const;
export type PartKey = (typeof PART_KEYS)[number];
export const PART_LABEL: Record<PartKey, string> = {
  morning: "Morning",
  midday: "Midday",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Night",
};

/** Each part's From and To on the clock ("HH:MM"); a part whose To is before its From crosses midnight. */
export type DayParts = Record<PartKey, { from: string; to: string }>;

export const DEFAULT_PARTS: DayParts = {
  morning: { from: "06:00", to: "11:00" },
  midday: { from: "11:00", to: "14:00" },
  afternoon: { from: "14:00", to: "17:00" },
  evening: { from: "17:00", to: "21:00" },
  night: { from: "21:00", to: "06:00" },
};

const minutesOf = (t: string) => +t.slice(0, 2) * 60 + +t.slice(3, 5);
const hhmm = (m: number) =>
  `${String(Math.floor(m / 60) % 24).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;

/**
 * A clock time as typed: "9:00 AM", "9 am", "21:30" or "09:00". Answers "HH:MM", or null when it
 * isn't a time.
 */
export function readClock(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const m = /^\s*(\d{1,2})(?::(\d{2}))?\s*(am|pm|a\.m\.|p\.m\.)?\s*$/i.exec(v);
  if (!m) return null;
  let h = +(m[1] as string);
  const min = m[2] === undefined ? 0 : +m[2];
  if (min > 59) return null;
  if (m[3]) {
    if (h < 1 || h > 12) return null;
    h = (h % 12) + (/^p/i.test(m[3]) ? 12 : 0);
  } else if (m[2] === undefined || h > 23) return null;
  return hhmm(h * 60 + min);
}

/** "9:00 AM", "9:30 PM". */
export function clockWords(t: string): string {
  const h = +t.slice(0, 2);
  return `${h % 12 || 12}:${t.slice(3, 5)} ${h < 12 ? "AM" : "PM"}`;
}

/** A part's hours as the mockup writes them: "6–11 AM", "11 AM–2 PM", "9 PM–6 AM". */
export function rangeWords(r: { from: string; to: string }): string {
  const short = (t: string) => {
    const h = +t.slice(0, 2);
    const m = t.slice(3, 5);
    return { n: `${h % 12 || 12}${m === "00" ? "" : `:${m}`}`, ap: h < 12 ? "AM" : "PM" };
  };
  const a = short(r.from);
  const b = short(r.to);
  return a.ap === b.ap ? `${a.n}–${b.n} ${b.ap}` : `${a.n} ${a.ap}–${b.n} ${b.ap}`;
}

/** The setting read safely from its row: anything missing or wrong takes the defaults. */
export function partsOf(raw: unknown): DayParts {
  const read = readParts(raw);
  return read.ok ? read.value : DEFAULT_PARTS;
}

/**
 * Reads the parts of the day: each part's From and To as clock times. Ranges can't overlap and
 * together cover the day (one of them may cross midnight).
 */
export function readParts(input: unknown): Read<DayParts> {
  const b = obj(input);
  if (!b) return no("Send the parts of the day.");
  const out = {} as DayParts;
  const owner: (PartKey | null)[] = Array.from({ length: 1440 }, () => null);
  for (const k of PART_KEYS) {
    const p = obj(b[k]) ?? {};
    const from = readClock(p.from);
    const to = readClock(p.to);
    if (!from || !to) return no(`${PART_LABEL[k]}: give From and To as times, such as 6:00 AM.`);
    if (from === to) return no(`${PART_LABEL[k]}: From and To can't be the same.`);
    out[k] = { from, to };
    const a = minutesOf(from);
    const len = (minutesOf(to) - a + 1440) % 1440;
    for (let i = 0; i < len; i++) {
      const at = (a + i) % 1440;
      const had = owner[at];
      if (had) return no(`${PART_LABEL[had]} and ${PART_LABEL[k]} overlap.`);
      owner[at] = k;
    }
  }
  const gap = owner.findIndex((x) => x === null);
  if (gap >= 0)
    return no(`The parts must cover the whole day. Nothing covers ${clockWords(hhmm(gap))}.`);
  return { ok: true, value: out };
}

// ---- A task's schedule -------------------------------------------------------------------------------

export const PARTS_OF_DAY = ["any", ...PART_KEYS, "at_time"] as const;
export type PartOfDay = (typeof PARTS_OF_DAY)[number];
export const PART_OF_DAY_LABEL: Record<PartOfDay, string> = {
  any: "Any time during the visit",
  ...PART_LABEL,
  at_time: "At a set time",
};

export const OFTEN = ["every_visit", "days", "times_week", "weekly", "as_needed"] as const;
export type Often = (typeof OFTEN)[number];
export const OFTEN_LABEL: Record<Often, string> = {
  every_visit: "Every visit",
  days: "On chosen days",
  times_week: "Times a week, any day",
  weekly: "Once a week on a day",
  as_needed: "As needed",
};

export const WINDOWS = [30, 60, 120] as const;
export const WINDOW_LABEL: Record<number, string> = { 30: "30 min", 60: "1 hr", 120: "2 hrs" };

export interface TaskSchedule {
  partOfDay: PartOfDay;
  /** "HH:MM", only with at_time. */
  atTime: string | null;
  /** 30, 60 or 120 minutes either side of the time, only with at_time. */
  windowMinutes: number | null;
  often: Often;
  /** Weekdays 0 (Sunday) to 6: some for days, exactly one for weekly, none otherwise. */
  days: number[];
  /** 1 to 7, only with times_week. */
  timesPerWeek: number | null;
}

export const EVERY_VISIT: TaskSchedule = {
  partOfDay: "any",
  atTime: null,
  windowMinutes: null,
  often: "every_visit",
  days: [],
  timesPerWeek: null,
};

/**
 * Reads the sheet's two choice groups: when in the day (a set time needs a time and a window) and
 * how often (chosen days need a day, once a week needs exactly one, times a week is 1 to 7).
 * Anything that doesn't go with the choice is left off.
 */
export function readTaskSchedule(input: unknown): Read<TaskSchedule> {
  const b = obj(input) ?? {};
  const part = b.partOfDay ?? "any";
  if (!(PARTS_OF_DAY as readonly unknown[]).includes(part)) return no("Pick when in the day.");
  const often = b.often ?? "every_visit";
  if (!(OFTEN as readonly unknown[]).includes(often)) return no("Pick how often.");
  const out: TaskSchedule = { ...EVERY_VISIT, partOfDay: part as PartOfDay, often: often as Often };
  if (part === "at_time") {
    const at = readClock(b.atTime);
    if (!at) return no("Give the time, such as 9:00 AM.");
    const w = Number(b.windowMinutes);
    if (!(WINDOWS as readonly number[]).includes(w)) return no("Pick the window.");
    out.atTime = at;
    out.windowMinutes = w;
  }
  if (often === "days" || often === "weekly") {
    const raw = Array.isArray(b.days) ? b.days : [];
    if (!raw.every((d) => Number.isInteger(d) && d >= 0 && d <= 6))
      return no("Days are Sunday to Saturday.");
    const days = [...new Set(raw as number[])].sort((x, y) => x - y);
    if (often === "days" && !days.length) return no("Pick at least one day.");
    if (often === "weekly" && days.length !== 1) return no("Pick the one day of the week.");
    out.days = days;
  }
  if (often === "times_week") {
    const n = Number(b.timesPerWeek);
    if (!Number.isInteger(n) || n < 1 || n > 7) return no("Times a week is 1 to 7.");
    out.timesPerWeek = n;
  }
  return { ok: true, value: out };
}

/** The columns of a task line as stored (migration 0038). */
export interface ScheduleColumns {
  part_of_day?: string | null;
  at_time?: string | null;
  window_minutes?: number | null;
  often?: string | null;
  days?: readonly number[] | null;
  times_per_week?: number | null;
}

/** A task line's schedule from its columns (a line from before 0038 is every visit, any time). */
export function scheduleOf(l: ScheduleColumns): TaskSchedule {
  return {
    partOfDay: (PARTS_OF_DAY as readonly unknown[]).includes(l.part_of_day)
      ? (l.part_of_day as PartOfDay)
      : "any",
    atTime: l.at_time ?? null,
    windowMinutes: l.window_minutes ?? null,
    often: (OFTEN as readonly unknown[]).includes(l.often) ? (l.often as Often) : "every_visit",
    days: [...(l.days ?? [])].map(Number),
    timesPerWeek: l.times_per_week ?? null,
  };
}

/** The columns to store for a schedule (a service or precaution line keeps them empty). */
export const scheduleColumns = (s: TaskSchedule | null) =>
  s
    ? {
        part_of_day: s.partOfDay,
        at_time: s.atTime,
        window_minutes: s.windowMinutes,
        often: s.often,
        days: s.days,
        times_per_week: s.timesPerWeek,
      }
    : {
        part_of_day: null,
        at_time: null,
        window_minutes: null,
        often: null,
        days: null,
        times_per_week: null,
      };

const partWords = (s: TaskSchedule) =>
  s.partOfDay === "at_time"
    ? `at ${clockWords(s.atTime ?? "09:00")}${s.windowMinutes ? ` (±${WINDOW_LABEL[s.windowMinutes]})` : ""}`
    : s.partOfDay === "any"
      ? ""
      : s.partOfDay;

/**
 * The plain label: "Every visit", "Mon, Wed, Fri · morning", "3 times a week", "Fri each week",
 * "Every visit · at 9:00 AM (±1 hr)", "As needed".
 */
export function taskLabel(s: TaskSchedule): string {
  const often =
    s.often === "days"
      ? s.days.map((d) => DAY_NAMES[d]).join(", ")
      : s.often === "weekly"
        ? `${DAY_NAMES[s.days[0] ?? 0]} each week`
        : s.often === "times_week"
          ? s.timesPerWeek === 1
            ? "Once a week"
            : `${s.timesPerWeek} times a week`
          : OFTEN_LABEL[s.often];
  const part = partWords(s);
  return part ? `${often} · ${part}` : often;
}

// ---- Due this visit --------------------------------------------------------------------------------------

export interface Span {
  starts_at: Date;
  ends_at: Date;
}

const overlap = (a: Span, from: Date, to: Date) =>
  Math.max(
    0,
    Math.min(a.ends_at.getTime(), to.getTime()) - Math.max(a.starts_at.getTime(), from.getTime()),
  );

/** One stretch a task falls in: a part of the day on a day, or a set time's window. */
interface Stretch {
  day: string;
  from: Date;
  to: Date;
  /** The part's start, or the set time itself. */
  anchor: Date;
}

/** The stretches near a visit (the day before to the day after, in the company's zone). */
function stretchesNear(s: TaskSchedule, v: Span, parts: DayParts, zone: string): Stretch[] {
  const d0 = zonedDay(v.starts_at, zone);
  const out: Stretch[] = [];
  for (const day of [addDays(d0, -1), d0, addDays(d0, 1)]) {
    if (s.partOfDay === "at_time") {
      const anchor = zonedInstant(day, s.atTime ?? "09:00", zone);
      const w = (s.windowMinutes ?? 0) * MINUTE;
      out.push({
        day,
        anchor,
        from: new Date(anchor.getTime() - w),
        to: new Date(anchor.getTime() + w),
      });
    } else if (s.partOfDay !== "any") {
      const p = parts[s.partOfDay];
      const from = zonedInstant(day, p.from, zone);
      const to = zonedInstant(
        minutesOf(p.to) <= minutesOf(p.from) ? addDays(day, 1) : day,
        p.to,
        zone,
      );
      out.push({ day, anchor: from, from, to });
    }
  }
  return out;
}

/**
 * Which of the visits overlapping a stretch it goes to: the one overlapping it most; a tie goes
 * to the one covering its start (a part's start, or the set time itself), then the earlier one.
 */
function ownerOf<T extends Span>(st: Stretch, visits: readonly T[]): T | null {
  const on = visits.filter((v) => overlap(v, st.from, st.to) > 0);
  if (!on.length) return null;
  const a = st.anchor.getTime();
  const covers = (v: T) => (v.starts_at.getTime() <= a && a < v.ends_at.getTime() ? 1 : 0);
  return (
    [...on].sort(
      (x, y) =>
        overlap(y, st.from, st.to) - overlap(x, st.from, st.to) ||
        covers(y) - covers(x) ||
        x.starts_at.getTime() - y.starts_at.getTime(),
    )[0] ?? null
  );
}

const onItsDays = (s: TaskSchedule, day: string) =>
  s.often !== "days" && s.often !== "weekly" ? true : s.days.includes(dowOf(day));

/**
 * Whether a task is due on a visit. `otherVisitsThatDay` are the patient's other visits around
 * it (split shifts, 24-hour care); `doneThisWeek` is how often a times-a-week task was done on
 * the patient's earlier visits this company week.
 */
export function dueOnVisit(
  task: { schedule: TaskSchedule; doneThisWeek?: number },
  visit: Span,
  otherVisitsThatDay: readonly Span[],
  parts: DayParts,
  zone: string,
): boolean {
  const s = task.schedule;
  if (s.often === "as_needed") return false;
  if (s.often === "times_week" && (task.doneThisWeek ?? 0) >= (s.timesPerWeek ?? 1)) return false;
  if (s.partOfDay === "any") return onItsDays(s, zonedDay(visit.starts_at, zone));
  const all: Span[] = [visit, ...otherVisitsThatDay];
  return stretchesNear(s, visit, parts, zone).some(
    (st) => onItsDays(s, st.day) && ownerOf(st, all) === visit,
  );
}

/**
 * When an Other care tasks line is next, in words: "Next: Mon · morning", from the patient's
 * coming visits (the first one it's due on), else from its days. As needed says so.
 */
export function nextDue(
  task: { schedule: TaskSchedule; doneThisWeek?: number },
  today: string,
  schedule: { visits: readonly Span[]; parts: DayParts; zone: string },
): string {
  const s = task.schedule;
  if (s.often === "as_needed") return "As needed";
  const part = partWords(s);
  const tail = part ? ` · ${part}` : "";
  const sorted = [...schedule.visits].sort((a, b) => a.starts_at.getTime() - b.starts_at.getTime());
  for (const v of sorted) {
    const others = sorted.filter((o) => o !== v);
    // A times-a-week task counts afresh on a coming visit only if its week isn't met yet.
    if (dueOnVisit({ schedule: s, doneThisWeek: 0 }, v, others, schedule.parts, schedule.zone)) {
      const day = zonedDay(v.starts_at, schedule.zone);
      return `Next: ${day === today ? "today" : DAY_NAMES[dowOf(day)]}${tail}`;
    }
  }
  if (s.often === "days" || s.often === "weekly") {
    for (let i = 1; i <= 7; i++) {
      const day = addDays(today, i);
      if (s.days.includes(dowOf(day))) return `Next: ${DAY_NAMES[dowOf(day)]}${tail}`;
    }
  }
  return taskLabel(s);
}

/**
 * A times-a-week task is behind as soon as the visits left in the company week can't make up the
 * count (each visit can do it once). A flag on the patient and To validate; care is never held.
 */
export function behindThisWeek(
  task: { schedule: TaskSchedule },
  doneThisWeek: number,
  visitsLeftThisWeek: number,
): boolean {
  const s = task.schedule;
  if (s.often !== "times_week") return false;
  return (s.timesPerWeek ?? 0) - doneThisWeek > visitsLeftThisWeek;
}

/** "Shower behind this week · 1 of 3 done, 1 visit left". */
export const behindWords = (what: string, n: number, done: number, left: number) =>
  `${what} behind this week · ${done} of ${n} done, ${left} visit${left === 1 ? "" : "s"} left`;

// ---- The lines in effect for a visit ---------------------------------------------------------------------

/** The parts of a care plan line the checklist reads. */
export interface TaskLineLike extends ScheduleColumns {
  id: string;
  kind: string;
  what: string;
  position?: number;
  replaces?: string | null;
  added_at?: Date;
  archived_at: Date | null;
}

/**
 * The task lines a visit reads, as they were at `at` (its check-in, or now when it hasn't
 * started): a line archived by then is gone, and a line that replaced another counts only from
 * when it was added. So a changed schedule on a signed plan starts with the next visit, and a
 * visit already checked in or done keeps the line it had.
 */
export function linesInEffect<T extends TaskLineLike>(lines: readonly T[], at: Date): T[] {
  return lines
    .filter(
      (l) =>
        l.kind === "task" &&
        (!l.archived_at || l.archived_at.getTime() > at.getTime()) &&
        (!l.replaces || !l.added_at || l.added_at.getTime() <= at.getTime()),
    )
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}

/** The line and every line before or after it in its chain of changes. */
export function chainOf(id: string, lines: readonly { id: string; replaces?: string | null }[]) {
  const root = (x: string): string => {
    const seen = new Set<string>();
    let at = x;
    for (;;) {
      const l = lines.find((y) => y.id === at);
      if (!l?.replaces || seen.has(at)) return at;
      seen.add(at);
      at = l.replaces;
    }
  };
  const r = root(id);
  return new Set(lines.filter((l) => root(l.id) === r).map((l) => l.id));
}

// ---- The visit's checklist --------------------------------------------------------------------------------

export interface ChecklistVisit extends Span {
  id: string;
  status: string;
  kind?: string;
}

export interface ChecklistResult {
  visit_id: string;
  plan_line_id: string;
  result: string;
  extra?: boolean;
}

export interface ChecklistTask {
  id: string;
  task: string;
  label: string;
  schedule: TaskSchedule;
  /** Marked Done today on this visit when it wasn't due. */
  extra: boolean;
}

/**
 * A visit's care tasks: those due this visit (with any marked Done today when they weren't due)
 * and the other care tasks on the plan. `patientVisits` are the patient's visits (any status);
 * `week` is the company week the visit is in.
 */
export function visitChecklist<T extends TaskLineLike>(x: {
  plan: { status: string } | null;
  lines: readonly T[];
  visit: ChecklistVisit;
  at: Date;
  patientVisits: readonly (ChecklistVisit & { day: string })[];
  results: readonly ChecklistResult[];
  parts: DayParts;
  zone: string;
  week: { start: string; end: string };
}): { due: ChecklistTask[]; other: ChecklistTask[] } {
  if (!x.plan || x.plan.status !== "signed") return { due: [], other: [] };
  const live = (v: ChecklistVisit) => v.status !== "cancelled" && v.status !== "missed";
  const around = x.patientVisits.filter(
    (o) =>
      o.id !== x.visit.id &&
      live(o) &&
      o.kind !== "orientation" &&
      Math.abs(o.starts_at.getTime() - x.visit.starts_at.getTime()) < 2 * 86_400_000,
  );
  // An orientation visit shadows a care visit: it reads its tasks as if it were the only one.
  const others = x.visit.kind === "orientation" ? [] : around;
  const earlier = x.patientVisits.filter(
    (o) =>
      o.id !== x.visit.id &&
      live(o) &&
      o.day >= x.week.start &&
      o.day <= x.week.end &&
      o.starts_at.getTime() < x.visit.starts_at.getTime(),
  );
  const due: ChecklistTask[] = [];
  const other: ChecklistTask[] = [];
  for (const l of linesInEffect(x.lines, x.at)) {
    const schedule = scheduleOf(l);
    const chain = chainOf(l.id, x.lines);
    const doneThisWeek = x.results.filter(
      (r) =>
        r.result === "done" &&
        chain.has(r.plan_line_id) &&
        earlier.some((o) => o.id === r.visit_id),
    ).length;
    const mine = x.results.find((r) => r.visit_id === x.visit.id && r.plan_line_id === l.id);
    const t: ChecklistTask = {
      id: l.id,
      task: l.what,
      label: taskLabel(schedule),
      schedule,
      extra: !!mine?.extra,
    };
    // A task already marked on this visit stays on its list (Done today included).
    if (mine) due.push(t);
    else if (dueOnVisit({ schedule, doneThisWeek }, x.visit, others, x.parts, x.zone)) due.push(t);
    else other.push(t);
  }
  return { due, other };
}

/**
 * The week's tally for a times-a-week task: done on the patient's visits that week, and the
 * visits left that haven't been checked out.
 */
export function weekTally<T extends TaskLineLike>(
  line: T,
  lines: readonly T[],
  weekVisits: readonly ChecklistVisit[],
  results: readonly ChecklistResult[],
): { done: number; left: number } {
  const chain = chainOf(line.id, lines);
  const doneOn = new Set(
    results.filter((r) => r.result === "done" && chain.has(r.plan_line_id)).map((r) => r.visit_id),
  );
  const live = weekVisits.filter(
    (v) => v.status !== "cancelled" && v.status !== "missed" && v.kind !== "orientation",
  );
  return {
    done: live.filter((v) => doneOn.has(v.id)).length,
    left: live.filter(
      (v) => (v.status === "scheduled" || v.status === "in_progress") && !doneOn.has(v.id),
    ).length,
  };
}

/**
 * The parts of the day a visit overlaps, in order: "morning and midday" for 9:00 AM to 1:00 PM.
 */
export function partsCovered(visit: Span, parts: DayParts, zone: string): PartKey[] {
  return PART_KEYS.filter((k) =>
    stretchesNear({ ...EVERY_VISIT, partOfDay: k }, visit, parts, zone).some(
      (st) => overlap(visit, st.from, st.to) > 0,
    ),
  );
}

/** "morning and midday", "evening, night and morning". */
export function listWords(xs: readonly string[]): string {
  if (xs.length <= 1) return xs[0] ?? "";
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

// ---- After the visit ----------------------------------------------------------------------------------------

/**
 * The flag for due tasks marked Not done or Refused: "1 task due, not done (reason given)", or
 * "(no reason)" when one has no reason. Null when every due task was done or isn't marked yet.
 */
export function notDoneWords(
  due: readonly { id: string }[],
  results: readonly { plan_line_id: string; result: string; note: string | null }[],
): string | null {
  const missed = results.filter(
    (r) => r.result !== "done" && due.some((t) => t.id === r.plan_line_id),
  );
  if (!missed.length) return null;
  const n = missed.length;
  const why = missed.every((r) => r.note?.trim()) ? "reason given" : "no reason";
  return `${n} task${n === 1 ? "" : "s"} due, not done (${why})`;
}

/** Also done this visit: free text up to 300 characters. */
export function readAlsoDone(input: unknown): Read<string | null> {
  const b = obj(input) ?? {};
  const v = typeof b.alsoDone === "string" ? b.alsoDone.replace(/\s+/g, " ").trim() : "";
  if (v.length > 300) return no("Also done this visit: up to 300 characters.");
  return { ok: true, value: v || null };
}
